import React, { useEffect, useRef } from 'react';

interface Dust {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  opacity: number;
}

interface Light {
  x: number;
  width: number;
  hue: number;
}

const MonsterTruckBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let dust: Dust[] = [];
    let lights: Light[] = [];
    let truckX = 0;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) renderStatic();
    };

    function groundY() {
      return canvas.height * 0.78;
    }

    function initScene() {
      truckX = canvas.width * 0.2;
      dust = Array.from({ length: 35 }, () => ({
        x: Math.random() * canvas.width,
        y: groundY() + Math.random() * (canvas.height - groundY()),
        size: Math.random() * 3 + 1,
        speed: Math.random() * 0.5 + 0.2,
        drift: Math.random() * 0.6 + 0.2,
        opacity: Math.random() * 0.3 + 0.1,
      }));
      lights = [
        { x: canvas.width * 0.12, width: canvas.width * 0.22, hue: 60 },
        { x: canvas.width * 0.88, width: canvas.width * 0.22, hue: 90 },
      ];
    }

    function drawSky() {
      const sky = ctx.createLinearGradient(0, 0, 0, canvas.height);
      sky.addColorStop(0, '#0a0d05');
      sky.addColorStop(0.6, '#141a09');
      sky.addColorStop(1, '#1f1405');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawStadiumLights() {
      lights.forEach((l) => {
        const grad = ctx.createLinearGradient(l.x, 0, l.x, groundY());
        grad.addColorStop(0, `hsla(${l.hue}, 90%, 70%, 0.18)`);
        grad.addColorStop(1, `hsla(${l.hue}, 90%, 60%, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(l.x, 0);
        ctx.lineTo(l.x - l.width / 2, groundY());
        ctx.lineTo(l.x + l.width / 2, groundY());
        ctx.closePath();
        ctx.fill();

        // Lamp glow at top
        const lamp = ctx.createRadialGradient(l.x, 0, 0, l.x, 0, 30);
        lamp.addColorStop(0, 'rgba(255, 255, 220, 0.9)');
        lamp.addColorStop(1, 'rgba(255, 255, 220, 0)');
        ctx.fillStyle = lamp;
        ctx.beginPath();
        ctx.arc(l.x, 0, 30, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function drawDirt() {
      const base = groundY();
      ctx.fillStyle = '#3a2410';
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);
      ctx.lineTo(0, base);
      // Rolling dirt mounds / ramps
      for (let x = 0; x <= canvas.width; x += 20) {
        const mound =
          Math.sin(x * 0.006) * 30 + Math.sin(x * 0.018 + 1) * 18;
        ctx.lineTo(x, base - mound);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.closePath();
      ctx.fill();

      // A bigger jump ramp
      ctx.fillStyle = '#4a2e14';
      ctx.beginPath();
      ctx.moveTo(canvas.width * 0.62, base);
      ctx.quadraticCurveTo(
        canvas.width * 0.78,
        base - 90,
        canvas.width * 0.86,
        base
      );
      ctx.closePath();
      ctx.fill();
    }

    function moundAt(x: number) {
      return Math.sin(x * 0.006) * 30 + Math.sin(x * 0.018 + 1) * 18;
    }

    function drawTruck(x: number) {
      const hop = Math.abs(Math.sin(time * 2)) * 26;
      const y = groundY() - moundAt(x) - hop;
      const tilt = Math.sin(time * 2) * 0.08;
      const w = 90;
      const h = 34;
      const wheelR = 22;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(tilt);

      // Body
      ctx.fillStyle = '#84cc16';
      roundRect(-w / 2, -h - wheelR * 0.4, w, h, 8);
      ctx.fill();
      // Cab
      ctx.fillStyle = '#65a30d';
      roundRect(-w / 2 + 14, -h - wheelR * 0.4 - 18, w * 0.5, 20, 6);
      ctx.fill();
      // Windshield
      ctx.fillStyle = 'rgba(186, 230, 253, 0.85)';
      roundRect(-w / 2 + 20, -h - wheelR * 0.4 - 14, w * 0.32, 12, 3);
      ctx.fill();

      // Wheels
      ctx.fillStyle = '#1c1917';
      [-w / 2 + wheelR, w / 2 - wheelR].forEach((wx) => {
        ctx.beginPath();
        ctx.arc(wx, -wheelR * 0.3, wheelR, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#a3a3a3';
        ctx.beginPath();
        ctx.arc(wx, -wheelR * 0.3, wheelR * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#1c1917';
      });

      ctx.restore();
    }

    function roundRect(x: number, y: number, w: number, h: number, r: number) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }

    function drawDust(d: Dust) {
      ctx.fillStyle = `rgba(168, 130, 80, ${d.opacity})`;
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2);
      ctx.fill();
    }

    function updateDust(d: Dust) {
      d.x -= d.drift;
      d.y -= d.speed * 0.2;
      if (d.x < 0) {
        d.x = canvas.width;
        d.y = groundY() + Math.random() * (canvas.height - groundY());
      }
    }

    function renderStatic() {
      drawSky();
      drawStadiumLights();
      drawDirt();
      drawTruck(canvas.width * 0.3);
      dust.forEach(drawDust);
    }

    function animate() {
      if (reducedMotion) return;
      time += 0.016;
      truckX += 1.4;
      if (truckX > canvas.width + 80) truckX = -80;

      drawSky();
      drawStadiumLights();
      drawDirt();
      drawTruck(truckX);
      dust.forEach((d) => {
        updateDust(d);
        drawDust(d);
      });
      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);
      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
};

export default MonsterTruckBackground;
